/**
 * @file popup-service.ts
 * @module app/modules/_common/components/pop-up/popup-service
 * @description Service for displaying various types of popups (info, success, error, warning).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.1
 * @version 2.1.2
 */

import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import '../../styles/pop-up.css';

export type PopupVariant = 'info' | 'success' | 'error' | 'warning';
type PopupConfig<Options> = Options extends SweetAlertOptions
  ? Omit<Options, 'icon'> & { message?: string }
  : never;
export type PopupOptions = PopupConfig<SweetAlertOptions>;

const popupSwal = withReactContent(Swal);

const popupTitles: Record<PopupVariant, string> = {
  info: 'Information',
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
};

const popupClasses = {
  popup: 'uw-popup',
  title: 'uw-popup__title',
  htmlContainer: 'uw-popup__content',
  actions: 'uw-popup__actions',
  confirmButton: 'uw-btn uw-btn-primary',
  cancelButton: 'uw-btn uw-btn-light',
  denyButton: 'uw-btn uw-btn-danger',
};

export const showPopup = (
  variant: PopupVariant,
  options: PopupOptions | string = {},
): Promise<SweetAlertResult<unknown>> => {
  const normalized = typeof options === 'string' ? { message: options } : options;
  const { message, customClass, ...overrides } = normalized;
  return popupSwal.fire<unknown>({
    heightAuto: false,
    buttonsStyling: false,
    title: popupTitles[variant],
    confirmButtonText: 'OK',
    showCloseButton: true,
    focusConfirm: false,
    ...(message === undefined ? {} : { text: message }),
    ...overrides,
    icon: variant,
    customClass: { ...popupClasses, ...customClass },
  });
};

export const closePopup = () => popupSwal.close();